import { useState } from 'react'
import {
  type LocationFilterBanner,
  parseLocCodes,
  useAdminLocationsConfig,
  useUpdateLocationsConfig,
} from '../../../features/admin/use-admin-locations'
import { AdminPage, adminError } from './admin-bits'
import { Btn, Field, Inline, input } from './controls'
import { mono, Section } from './ui'

function LocField({
  banner,
  field,
  label,
  hint,
  saved,
}: {
  banner: LocationFilterBanner
  field: 'excludedLocs' | 'allowedLocs'
  label: string
  hint: string
  saved: string[]
}) {
  const update = useUpdateLocationsConfig()
  const [text, setText] = useState(saved.join(', '))
  const parsed = parseLocCodes(text)
  const dirty = parsed.join(',') !== saved.join(',')
  return (
    <div>
      <Field label={label} hint={hint}>
        <textarea
          className={`${input} h-24 resize-y border border-[var(--color-border)] px-2 py-1.5`}
          style={mono}
          value={text}
          onChange={(e) => setText(e.target.value)}
        />
      </Field>
      <div className="mt-2 flex items-center justify-between gap-3">
        <span className="text-[12px] italic text-[var(--color-muted)]">{parsed.length} codes</span>
        <div className="flex items-center gap-4">
          {update.error ? <Inline tone="bad">{adminError(update.error)}</Inline> : null}
          {update.isSuccess && !dirty ? <Inline tone="ok">Saved</Inline> : null}
          <Btn
            variant="primary"
            disabled={!dirty || update.isPending}
            onClick={() => update.mutate({ banner, [field]: parsed })}
          >
            {update.isPending ? 'Saving…' : 'Save'}
          </Btn>
        </div>
      </div>
    </div>
  )
}

export function LedgerAdminLocations() {
  const cfg = useAdminLocationsConfig()
  const banners: [LocationFilterBanner, string][] = [
    ['dtlr', 'DTLR'],
    ['spc', 'SPC'],
  ]
  return (
    <AdminPage
      title="Location filters"
      standfirst="Hide or restrict store codes per banner. Separate codes with commas, spaces or new lines."
      loading={cfg.isLoading}
      error={!!cfg.error}
    >
      {banners.map(([b, label]) => (
        <Section key={b} title={label}>
          <div className="grid gap-10 pt-5 md:grid-cols-2">
            <LocField
              banner={b}
              field="excludedLocs"
              label="Excluded locations"
              hint="Never shown for this banner."
              saved={cfg.data?.[b].excludedLocs ?? []}
            />
            <LocField
              banner={b}
              field="allowedLocs"
              label="Allowed locations"
              hint="When set, only these are shown."
              saved={cfg.data?.[b].allowedLocs ?? []}
            />
          </div>
        </Section>
      ))}
    </AdminPage>
  )
}
